import React, { useContext } from 'react';
import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { Form, Formik } from 'formik';
import { useMutation } from '@apollo/client';
import * as Yup from 'yup';
import get from 'lodash/get';

import Input from '../../../components/Input';
import { SEND_HUB_COUNTER_WAITLIST_EMAIL, SendHubCounterWaitlistEmailMutation } from '../query';
import { useCustomToast } from '../../../hooks/useCustomToast';
import { useSpinner } from '../../../contexts/SpinnerContext';
import UserContext from '../../../contexts/UserContext';

interface SendHubCounterWaitlistEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  dropPointId: string;
  refetch: () => Promise<any> | void;
  refetchState?: () => Promise<any> | void;
}

const SendHubCounterWaitlistEmailSchema = Yup.object().shape({
  comment: Yup.string().required('Du musst einen Kommentar/Begründung eingeben'),
});

const SendHubCounterWaitlistEmailModal = ({
  isOpen,
  onClose,
  dropPointId,
  refetch,
  refetchState,
}: SendHubCounterWaitlistEmailModalProps) => {
  const { user: internalUser } = useContext(UserContext);
  const { toggleSpinner } = useSpinner();
  const toast = useCustomToast();
  const [sendHubCounterWaitlistEmail, { loading }] = useMutation<SendHubCounterWaitlistEmailMutation>(SEND_HUB_COUNTER_WAITLIST_EMAIL);

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>218 Email senden</ModalHeader>
        <Formik
          initialValues={{ comment: '' }}
          validationSchema={SendHubCounterWaitlistEmailSchema}
          onSubmit={async ({ comment }) => {
            try {
              toggleSpinner(true);
              await sendHubCounterWaitlistEmail({
                variables: { dropPointId, comment, internalUserId: internalUser.id },
              });
              await Promise.all([Promise.resolve(refetch()), Promise.resolve(refetchState?.())]);
              toast({
                title: 'Erfolg!',
                description: 'Die 218 Email wurde an den DropPoint-Inhaber gesendet.',
                status: 'success',
              });
              onClose();
            } catch (error) {
              toast({
                description: get(error, 'graphQLErrors.0.message', 'Ein Fehler ist aufgetreten!'),
                status: 'error',
              });
            } finally {
              toggleSpinner(false);
            }
          }}
        >
          <Form>
            <ModalBody>
              <Text fontSize="sm" mb="4">
                Es wird die Wartelisten-Email (Template 218) an den DropPoint-Inhaber gesendet. Der Status bleibt unverändert.
              </Text>
              <Input name="comment" label="Kommentar/Begründung" />
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" size="sm" colorScheme="red" type="button" onClick={onClose}>
                Abbrechen
              </Button>
              <Button ml="4" size="sm" colorScheme="brand" type="submit" isLoading={loading}>
                Email senden
              </Button>
            </ModalFooter>
          </Form>
        </Formik>
        <ModalCloseButton />
      </ModalContent>
    </Modal>
  );
};

export default SendHubCounterWaitlistEmailModal;
